import { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useState } from 'react';
import { useUID } from 'react-uid';
import { MemberDTO } from '../backend/entities/Member';
import { CATEGORIES } from '../misc-types/category';
import ButtonWithFeedback from '../react-components/button-with-feedback';
import CategoryChooser from '../react-components/category/chooser';
import { CoreUIContext } from '../react-components/core-ui/provider';
import CompactLocationPicker from '../react-components/inputs/compact-location-picker';
import Password from '../react-components/inputs/password';
import Layout from '../react-components/layout';
import fullWidthCard from '../styles/full-width-card.module.css';
import inputGrid from '../styles/input-grid.module.css';
import { latLngToPoint } from '../utils/geolocation-format-conversion';
import { useErrorNotifier } from '../utils/hooks';
import { useRequest } from '../utils/make-api-request';

const MemberRegistration: NextPage = () => {
  const allCategoriesId = useUID();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [geolocation, setGeolocation] = useState<google.maps.LatLngLiteral | undefined>();
  const [interestCategories, setInterestCategories] = useState<string[]>([]);
  const [registrationRequest, postMember] = useRequest('members', 'post');
  const handleErrors = useErrorNotifier();
  const router = useRouter();
  const { showNotification } = useContext(CoreUIContext);
  function register() {
    const member: MemberDTO = {
      email,
      passwordHash: password,
      geolocation: geolocation ? latLngToPoint(geolocation) : null,
      interestCategories
    };
    return postMember(member);
  }
  return (
    <Layout>
      <Head>
        <title>Member Registration</title>
      </Head>
      <h1>Member Registration</h1>
      <form
        className={fullWidthCard.fullWidthCard}
        onSubmit={e => {
          e.preventDefault();
          register()
            .then(() => {
              router.push('/login/member');
              showNotification({
                title: 'Account Created',
                message: 'Check your email for a verification link.',
                style: 'success'
              });
            })
            .catch(handleErrors);
        }}
      >
        <fieldset>
          <legend>Account info</legend>
          <div className={inputGrid.inputGrid}>
            <input type='email' placeholder='Email' value={email} onChange={e => setEmail(e.target.value)} />
            <Password placeholder='Password' value={password} isNew onChange={e => setPassword(e.target.value)} />
          </div>
        </fieldset>
        <fieldset>
          <legend>Primary location</legend>
          <p className='mb-1'>
            Optionally, choose your home location so results are closer to you when searching for business, offers, or
            special deals near you.
          </p>
          <div style={{ maxWidth: 'max-content' }}>
            <CompactLocationPicker onChange={setGeolocation} startDirty />
          </div>
        </fieldset>
        <fieldset>
          <legend>Categories of Interest</legend>
          <p>If you choose to receive emails, they will be targeted toward these categories.</p>
          <CategoryChooser
            multiple
            selectedCategories={interestCategories}
            setSelectedCategories={setInterestCategories}
          />
          <input
            type='checkbox'
            id={allCategoriesId}
            checked={interestCategories.length === CATEGORIES.length}
            onChange={e => {
              if (e.target.checked) {
                setInterestCategories(CATEGORIES.map(c => c.slug));
              } else {
                setInterestCategories([]);
              }
            }}
          />
          <label htmlFor={allCategoriesId} className='my-2'>
            All
          </label>
        </fieldset>
        <ButtonWithFeedback request={registrationRequest} type='submit' className='primary'>
          Register
        </ButtonWithFeedback>
      </form>
    </Layout>
  );
};

export default MemberRegistration;
